import { motion } from "framer-motion";
import { Clock, Droplets, Package, Zap } from "lucide-react";

export const DirectionsForUse = () => {
  return (
    <section 
      className="py-24 md:py-32 bg-[#050505] border-y border-white/10"
      data-testid="directions-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-mono text-sm tracking-[0.3em] text-[#E82D00] mb-4"
          >
            DIRECTIONS FOR USE
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="directions-heading"
          >
            How to Use
          </motion.h2>
        </div>

        {/* Usage cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Daily Use */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-8 border border-white/10 bg-[#0A0A0A]"
            data-testid="daily-use-card"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="icon-container">
                <Clock size={24} strokeWidth={1.5} className="text-[#E82D00]" />
              </div>
              <h3 
                className="text-2xl font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Daily Use
              </h3>
            </div>
            
            <div className="space-y-4 text-[#A3A3A3]">
              <p className="text-lg">Take <span className="text-white font-semibold">2.5 – 5g daily</span></p>
              <p className="text-sm font-mono">1 scoop = 5g</p>
              <p>Mix with water, juice, or add to your workout drink.</p>
              <p className="text-sm">Creatine may also be taken with food.</p>
            </div>
          </motion.div>

          {/* Loading Phase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="p-8 border border-[#616556]/30 bg-[#616556]/5"
            data-testid="loading-phase-card"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="icon-container border-[#616556]/50">
                <Zap size={24} strokeWidth={1.5} className="text-[#E82D00]" />
              </div>
              <div>
                <span className="font-mono text-xs tracking-widest text-[#616556] uppercase block mb-1">Optional</span>
                <h3 
                  className="text-2xl font-bold uppercase tracking-tight text-white"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  Loading Phase
                </h3>
              </div>
            </div>
            
            <div className="space-y-4 text-[#A3A3A3]">
              <p className="text-sm text-[#E5E5E5]">For faster muscle saturation:</p>
              <p className="text-lg">Take <span className="text-white font-semibold">20g daily for 5–7 days</span></p>
              <p>Divide into four 5g servings.</p>
              <p className="text-sm">After loading, continue with 2.5 – 5g daily.</p>
            </div>
          </motion.div>
        </div>

        {/* Hydration guidance */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="p-8 border border-white/10 bg-[#0A0A0A]"
          data-testid="hydration-guidance"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="icon-container">
              <Droplets size={24} strokeWidth={1.5} className="text-[#E82D00]" />
            </div>
            <h3 
              className="text-xl font-bold uppercase tracking-tight text-white"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Hydration Guidance
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-1 h-1 bg-[#E82D00] mt-2 flex-shrink-0" />
              <p className="text-[#A3A3A3]">Daily consistency is more important than time of intake.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-1 h-1 bg-[#E82D00] mt-2 flex-shrink-0" />
              <p className="text-[#A3A3A3]">Many athletes prefer taking creatine post-training or with a carbohydrate-containing drink.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-1 h-1 bg-[#E82D00] mt-2 flex-shrink-0" />
              <p className="text-[#A3A3A3]">Ensure adequate daily fluid intake when using creatine.</p>
            </div>
          </div>
        </motion.div>

        {/* Storage info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 flex flex-wrap items-center gap-6"
        >
          <div className="flex items-center gap-3">
            <Package size={20} strokeWidth={1.5} className="text-[#A3A3A3]" />
            <span className="font-mono text-sm text-[#A3A3A3]">Store in a cool, dry place</span>
          </div>
          <div className="w-1 h-1 bg-[#A3A3A3] hidden md:block" />
          <span className="font-mono text-sm text-[#A3A3A3]">Keep container tightly closed</span>
          <div className="w-1 h-1 bg-[#A3A3A3] hidden md:block" />
          <span className="font-mono text-sm text-[#A3A3A3]">Keep out of reach of children</span>
        </motion.div>
      </div>
    </section>
  );
};

export default DirectionsForUse;
